import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import useDevice from '../../hooks/useDevice';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Bell, Loader2, AlertTriangle, RotateCw, Info } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';
import OnboardingStepIndicator from './OnboardingStepIndicator';

// iter97 (Feb 2026) — the notification step now:
//   • Explicitly explains that installing the app and allowing notifications
//     are TWO SEPARATE things (a common source of pilot support tickets).
//   • Always shows the phone-settings path for the selected platform (not
//     only after a denial). Users who previously denied on the same device
//     can immediately follow the steps to unblock without having to trigger
//     the browser prompt again.
//   • Provides an iPhone / Android / Desktop picker (pre-selected via
//     `useDevice()`), so a manager coaching a phone-first worker from
//     their desktop can preview the right screens.
//   • Keeps the primary "Turn on updates" button and the "Not now" escape.

function currentPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  try { return Notification.permission; } catch { return 'default'; }
}

const PLATFORMS = [
  { key: 'ios', label: 'iPhone' },
  { key: 'android', label: 'Android' },
  { key: 'desktop', label: 'Desktop' },
];

const SETTINGS_PATH = {
  ios: 'Settings → Notifications → ShiftCover365 → Allow Notifications',
  android: 'Settings → Apps → ShiftCover365 → Notifications → Allow',
  desktop: 'Browser Settings → Site Permissions → Notifications → Allow',
};

export default function EnableNotificationsStep({ onDone }) {
  const { api, user, refreshUser } = useAuth();
  const { device: detectedPlatform } = useDevice();
  const [platform, setPlatform] = useState(detectedPlatform);
  const [permission, setPermission] = useState(currentPermission);
  const [submitting, setSubmitting] = useState(false);
  const shownAuditFiredRef = useRef(false);

  const isManager = user?.role === 'manager';
  const stepIndex = isManager ? 5 : 4;

  useEffect(() => { setPlatform(detectedPlatform); }, [detectedPlatform]);

  useEffect(() => {
    if (shownAuditFiredRef.current) return;
    shownAuditFiredRef.current = true;
    api.post('/users/me/notification-status', { status: 'shown', platform: detectedPlatform }).catch(() => {});
  }, [api, detectedPlatform]);

  const recordAndAdvance = async (status) => {
    setSubmitting(true);
    try {
      await api.post('/users/me/notification-status', { status, platform: detectedPlatform });
      await refreshUser();
      if (typeof onDone === 'function') onDone();
    } catch (err) {
      toast.error(formatApiError(err, 'Something went wrong. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEnable = async () => {
    if (permission === 'unsupported') {
      await recordAndAdvance('dismissed');
      return;
    }
    setSubmitting(true);
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'granted') {
        try {
          const { subscribeToWebPush } = await import('../../lib/pushSubscribe');
          subscribeToWebPush(api).catch(() => {});
        } catch { /* very old browsers only */ }
        await recordAndAdvance('granted');
      } else if (result === 'denied') {
        toast('Notifications were blocked. Follow the device steps below to turn them on.', { duration: 4500 });
        setSubmitting(false);
      } else {
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(formatApiError(err, 'Could not request permission.'));
      setSubmitting(false);
    }
  };

  const heading = isManager ? 'Get worker updates' : 'Get shift updates';
  const supporting = isManager
    ? "We'll ping you when someone applies, cancels or sends a message."
    : "We'll let you know when a new shift is available or a manager picks you.";
  const accent = isManager ? '#0f1b3d' : '#DA291C';
  const showDeniedHelp = permission === 'denied';
  const platformSettingsPath = SETTINGS_PATH[platform] || SETTINGS_PATH.desktop;

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center p-4" data-testid="enable-notifications-step">
      <Card className="w-full max-w-md border-0 shadow-lg">
        <CardContent className="p-8">
          <OnboardingStepIndicator current={stepIndex} role={isManager ? 'manager' : 'worker'} />
          <div className="flex items-center gap-3 mb-3">
            <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}1a` }}>
              <Bell className="h-6 w-6" style={{ color: accent }} />
            </div>
            <h2 className="text-xl font-bold text-slate-900" data-testid="notifications-title">{heading}</h2>
          </div>
          <p className="text-base text-slate-600 mb-4" data-testid="notifications-supporting-text">{supporting}</p>

          {/* Two-step reminder — install + allow are separate. */}
          <div
            className="rounded-xl bg-blue-50 border border-blue-200 p-4 mb-4 flex items-start gap-3"
            data-testid="notifications-two-step-reminder"
          >
            <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-900 leading-relaxed">
              To receive shift updates, make sure ShiftCover365 is <span className="font-semibold">installed on your Home Screen</span> and <span className="font-semibold">notifications are allowed</span> in your device settings.
            </p>
          </div>

          {/* Device picker — pre-selects the detected platform. */}
          <div
            className="flex gap-1 p-1 mb-4 rounded-full bg-slate-100 border border-slate-200"
            role="tablist"
            data-testid="notif-platform-picker"
          >
            {PLATFORMS.map((p) => {
              const active = platform === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setPlatform(p.key)}
                  className={`flex-1 h-9 rounded-full text-sm font-semibold transition-colors ${active ? 'bg-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                  style={active ? { color: accent } : undefined}
                  data-testid={`notif-platform-${p.key}`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          {/* Always-visible device settings path — the fix a user needs
              whether they've denied before or are about to be asked. */}
          <div
            className="rounded-xl bg-slate-50 border border-slate-200 p-4 mb-4"
            data-testid={`notif-settings-path-${platform}`}
          >
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              Where to allow notifications
            </p>
            <p className="text-base text-slate-800 font-medium leading-relaxed">
              {platformSettingsPath}
            </p>
          </div>

          {/* Denied-help block only when the current device has blocked. */}
          {showDeniedHelp && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-4 flex items-start gap-3" data-testid="notif-denied-help">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold">Notifications are blocked on this device</p>
                <p className="mt-0.5">Follow the steps above, then tap <span className="font-semibold">Try again</span> below.</p>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <Button
              onClick={handleEnable}
              disabled={submitting}
              className="w-full h-12 rounded-full text-white text-base font-semibold disabled:opacity-60"
              style={{ backgroundColor: accent }}
              data-testid="notifications-enable-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : showDeniedHelp ? (
                <><RotateCw className="h-4 w-4 mr-1.5" /> Try again</>
              ) : (
                'Turn on updates'
              )}
            </Button>
            <Button
              onClick={() => recordAndAdvance(showDeniedHelp ? 'denied' : 'dismissed')}
              disabled={submitting}
              variant="ghost"
              className="w-full h-12 rounded-full text-base font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              data-testid="notifications-later-btn"
            >
              Not now
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
